import { Check, Copy, Download, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { BuilderConfig } from '~/lib/builder-types';
import type { Spec } from '~/lib/db/schema';
import type { FeatureWithDefinition } from '~/lib/export';
import { exportAsJson, exportAsPrompt } from '~/lib/export';
import { importFromJson } from '~/lib/import';

interface Props {
  spec: Spec;
  config: BuilderConfig;
  features: FeatureWithDefinition[];
  onClose: () => void;
  onImport?: (config: BuilderConfig) => void;
}

export default function ExportModal({
  spec,
  config,
  features,
  onClose,
  onImport,
}: Props) {
  const [tab, setTab] = useState<'prompt' | 'json'>('prompt');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const promptText = exportAsPrompt(spec, config, features);
  const jsonText = exportAsJson(spec, config, features);
  const content = tab === 'prompt' ? promptText : jsonText;

  async function copyToClipboard() {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const featureSlugs = features.map((f) => f.slug);
      const result = importFromJson(text, featureSlugs);
      if (result) {
        onImport?.(result);
        toast('Config imported successfully');
        onClose();
      } else {
        toast('Invalid config file — could not import');
      }
    };
    reader.readAsText(file);
    // Reset so same file can be re-imported
    e.target.value = '';
  }

  function downloadJson() {
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${spec.slug}-spec.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-semibold text-gray-900">Export Spec</h2>
            <p className="text-sm text-gray-500">{spec.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-1 px-5 pt-3">
          {(['prompt', 'json'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === t
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t === 'prompt' ? 'Prompt (Markdown)' : 'JSON'}
            </button>
          ))}
        </div>

        {/* Open in AI builder */}
        <div className="mx-5 mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs font-medium text-gray-600 mb-2">
            Open in AI builder
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              {
                name: 'Claude',
                url: 'https://claude.ai/new',
                color:
                  'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
              },
              {
                name: 'ChatGPT',
                url: 'https://chatgpt.com/',
                color:
                  'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
              },
              {
                name: 'v0',
                url: 'https://v0.dev/',
                color:
                  'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200',
              },
              {
                name: 'Bolt',
                url: 'https://bolt.new/',
                color:
                  'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
              },
            ].map(({ name, url, color }) => (
              <button
                key={name}
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(promptText);
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${color}`}
              >
                {name}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Prompt is copied to clipboard when you click
          </p>
        </div>

        <div className="flex-1 overflow-auto mx-5 my-3">
          <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg border border-gray-200 p-4 whitespace-pre-wrap font-mono leading-relaxed">
            {content}
          </pre>
        </div>

        <div className="flex items-center gap-2 px-5 py-4 border-t">
          <button
            type="button"
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          {tab === 'json' && (
            <>
              <button
                type="button"
                onClick={downloadJson}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download JSON
              </button>
              {onImport && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={handleImportFile}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    Import JSON
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
